import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useImgOnErrorControl from 'Hooks/useImgOnErrorControl';
import { useQueryUpdatedDate } from 'Hooks/activities/useQueryUpdatedDate';
import { getTheme } from 'Store/MultiDomain/MultiDomain.selector';
import {
  IDragAndDropDataFormat,
  ITopicDnDResponse,
} from 'Data/interfaces/Activities/IDragAndDropDataFormat';
import {
  IFiltroOptions,
  enumToStringValueMap,
} from 'Data/interfaces/Activities/IFilters';
import { INotificationInAppTopic } from 'Data/interfaces/Notification/INotification';
import {
  getColumnsTableActivities,
  IResizableColumnProps,
  minWidthResizableActivitiesColumns,
  RESIZABLE_ACTIVITIES_COLUMN_IDS,
} from './components/Columns/ListVision.columns';
import CustomEmptyData from './components/CustomEmptyData';
import {
  complexfilterActivities,
  filterActivities,
  sortActivities,
} from 'Utils/ActivitiesUtils';
import dayjs from 'dayjs';
import { IClearFiltersRequestUpdatedDate } from 'Data/interfaces/Activities/IFiltersRequest';
import { getIsCollapsed } from 'Store/Obra/Obra.selector';

export type TypeOrderType = "Identifier" |
  "Title" |
  "Type" |
  "Priority" |
  "ConstructionSiteDisciplines" |
  "TopicAssignedUsers" |
  "DueDate" |
  "Status";

interface IOrderOpt {
  type?: TypeOrderType;
  sortDirection?: boolean;
}

interface IUseListVision {
  csId: number;
  listActivities?: IDragAndDropDataFormat;
  height: number;
  searchActivity: string;
  filters?: Record<string, IFiltroOptions<ITopicDnDResponse>>;
  showFilters?: boolean;
  hasFilters: boolean;
  inAppTopicNotifications: INotificationInAppTopic[];
  width: number;
  onOpenCreateActivity: () => void;
  onOpenShowActivity: (activity: string) => void;
}

const useListVision = ({
  csId,
  listActivities,
  height,
  searchActivity,
  filters,
  showFilters,
  hasFilters,
  inAppTopicNotifications,
  width,
  onOpenCreateActivity,
  onOpenShowActivity,
}: IUseListVision) => {
  const { handleImgOnError, imgsOnErrorControl } = useImgOnErrorControl();
  const theme = useSelector(getTheme);
  const isShellCollapsed = useSelector(getIsCollapsed);

  const filterUpdatedDate = useMemo(() => {
    const filterData: IClearFiltersRequestUpdatedDate = { from: null, to: null };
    if (!filters) return filterData;

    const filterArray = Object.values(filters);
    const updatedDateFilter = filterArray.findIndex(filter => {
      return filter.topicId === 'UpdatedDate';
    });
    if (updatedDateFilter !== -1) {
      const filter = filterArray[updatedDateFilter];
      let value1 = null;
      let value2 = null;
      if (Array.isArray(filter.value) && filter.value.length > 1) {
        value1 = dayjs(filter.value[0]);
        value2 = dayjs(filter.value[1]);
      } else {
        value1 = dayjs(filter.value);
      }
      switch (filter.operation) {
        case 'equal':
          filterData.from = value1.format('YYYY-MM-DD');
          filterData.to = value1.format('YYYY-MM-DD');
          break;
        case 'between':
          if (value1 && value2) {
            filterData.from = value1.format('YYYY-MM-DD');
            filterData.to = value2.format('YYYY-MM-DD');
          }
          break;
        case 'before':
          filterData.to = value1.subtract(1, 'day').format('YYYY-MM-DD');
          break;
        case 'after':
          filterData.from = value1.add(1, 'day').format('YYYY-MM-DD');
          break;
        case 'onOrBefore':
          filterData.to = value1.format('YYYY-MM-DD');
          break;
        case 'onOrAfter':
          filterData.from = value1.format('YYYY-MM-DD');
          break;
      }
    }
    return filterData;
  }, [filters]);
  const {
    data: dataUpdatedDate,
    isFetching: isFetchingUpdatedDate
  } = useQueryUpdatedDate({
    csId,
    from: filterUpdatedDate.from,
    to: filterUpdatedDate.to
  });

  const [orderOpt, setOrderOpt] = useState<IOrderOpt | null>(null);
  const [currentAttachmentIndex, setCurrentAttachmentIndex] = useState(0);
  const [showPreview, setShowPreview] = useState<Map<string, boolean>>(new Map());
  const [columnsWidth, setColumnsWidth] = useState<IResizableColumnProps>({
    active: false,
    columns: {
      [RESIZABLE_ACTIVITIES_COLUMN_IDS.Identifier]: minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.Identifier],
      [RESIZABLE_ACTIVITIES_COLUMN_IDS.Visibility]: minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.Visibility],
      [RESIZABLE_ACTIVITIES_COLUMN_IDS.Title]: minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.Title],
      [RESIZABLE_ACTIVITIES_COLUMN_IDS.Type]: minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.Type],
      [RESIZABLE_ACTIVITIES_COLUMN_IDS.Priority]: minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.Priority],
      [RESIZABLE_ACTIVITIES_COLUMN_IDS.ConstructionSiteDisciplines]: minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.ConstructionSiteDisciplines],
      [RESIZABLE_ACTIVITIES_COLUMN_IDS.TopicAssignedUsers]: minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.TopicAssignedUsers],
      [RESIZABLE_ACTIVITIES_COLUMN_IDS.DueDate]: minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.DueDate],
      [RESIZABLE_ACTIVITIES_COLUMN_IDS.Status]: minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.Status],
    }
  });

  const leftBarAndPaddingWidth = width - (isShellCollapsed ? 102 : 278);
  const heightAndFiltersShow = height - 332.5 - (showFilters ? 41.5 : 0);

  const handlePreview = useCallback((id: string, visible: boolean) => {
    setShowPreview(prevState => {
      prevState.set(id, visible);
      return new Map(prevState);
    });
  }, []);

  const listActivitiesFormat = useMemo(() => {
    if (listActivities?.activities && JSON.stringify(listActivities.activities) !== '{}') {
      const arrayList = Object.values(listActivities.activities);
      return arrayList.sort(sortActivities);
    }
    return [];
  }, [listActivities]);

  const handleOrderOpt = (orderType?: TypeOrderType, sorterOrder?: boolean) => {
    setOrderOpt({ type: orderType, sortDirection: sorterOrder });
  }

  const columns = useMemo(() => {
    return getColumnsTableActivities({
      columnsWidth: columnsWidth.columns,
      searchActivity,
      theme,
      currentAttachmentIndex,
      imgsOnErrorControl,
      showPreview,
      inAppTopicNotifications,
      onOpenShowActivity,
      onOrderOpt: handleOrderOpt,
      setCurrentAttachmentIndex,
      onImgOnError: handleImgOnError,
      onPreview: handlePreview,
    });
  }, [
    columnsWidth.columns,
    searchActivity,
    theme,
    currentAttachmentIndex,
    imgsOnErrorControl,
    showPreview,
    inAppTopicNotifications,
    onOpenShowActivity,
    handleImgOnError,
    handlePreview,
  ]);

  const sorterActivitiesTable = useCallback((sortOrder?: boolean) => {
    return {
      Identifier: (a: ITopicDnDResponse, b: ITopicDnDResponse) => {
        const identifierA = Number(a.Identifier);
        const identifierB = Number(b.Identifier);

        if (identifierA && b.Identifier) {
          return sortOrder ? identifierB - identifierA : identifierA - identifierB;
        } else if (identifierA) {
          return -1;
        } else if (identifierB) {
          return 1;
        }
        return 0;
      },
      Title: (a: ITopicDnDResponse, b: ITopicDnDResponse) => {
        return a.Title.localeCompare(b.Title) * (sortOrder ? -1 : 1);
      },
      Priority: (a: ITopicDnDResponse, b: ITopicDnDResponse) => {
        if (a.Priority && b.Priority) {
          return sortOrder ? b.Priority - a.Priority : a.Priority - b.Priority;
        } else if (a.Priority) {
          return -1;
        } else if (b.Priority) {
          return 1;
        }
        return 0;
      },
      DueDate: (a: ITopicDnDResponse, b: ITopicDnDResponse) => {
        const dueDateA = a.DueDate ? new Date(a.DueDate).getTime() : 0;
        const dueDateB = b.DueDate ? new Date(b.DueDate).getTime() : 0;

        if (a.DueDate && b.DueDate) {
          return sortOrder ? dueDateB - dueDateA : dueDateA - dueDateB;
        } else if (a.DueDate) {
          return -1;
        } else if (b.DueDate) {
          return 1;
        }
        return 0;
      },
      ConstructionSiteDisciplines: (a: ITopicDnDResponse, b: ITopicDnDResponse) => {
        const disciplinesA = a.ConstructionSiteDisciplines || [];
        const disciplinesB = b.ConstructionSiteDisciplines || [];

        if (disciplinesA.length === 0 && disciplinesB.length === 0) {
          return 0;
        } else if (disciplinesA.length === 0) {
          return disciplinesB.length > 0 ? sortOrder ? -1 : 1 : 0;
        } else if (disciplinesB.length === 0) {
          return sortOrder ? 1 : -1;
        }

        const minLength = Math.min(disciplinesA.length, disciplinesB.length);

        for (let i = 0; i < minLength; i++) {
          const nameA = disciplinesA[i].CustomName || disciplinesA[i].Discipline.Name || '';
          const nameB = disciplinesB[i].CustomName || disciplinesB[i].Discipline.Name || '';
          const disciplineComparison = nameA.localeCompare(nameB) * (sortOrder ? -1 : 1);
          if (disciplineComparison !== 0) {
            return disciplineComparison;
          }
        }

        return sortOrder ? disciplinesB.length - disciplinesA.length : disciplinesA.length - disciplinesB.length;
      },
      TopicAssignedUsers: (a: ITopicDnDResponse, b: ITopicDnDResponse) => {
        const usersA = a.TopicAssignedUsers || [];
        const usersB = b.TopicAssignedUsers || [];

        if (usersA.length === 0 && usersB.length === 0) {
          return 0;
        } else if (usersA.length === 0) {
          return usersB.length > 0 ? sortOrder ? -1 : 1 : 0;
        } else if (usersB.length === 0) {
          return sortOrder ? 1 : -1;
        }

        const minLength = Math.min(usersA.length, usersB.length);

        for (let i = 0; i < minLength; i++) {
          const nameA = usersA[i].Nome || '';
          const nameB = usersB[i].Nome || '';
          const userComparison = nameA.localeCompare(nameB) * (sortOrder ? -1 : 1);
          if (userComparison !== 0) {
            return userComparison;
          }
        }

        return sortOrder ? usersB.length - usersA.length : usersA.length - usersB.length;
      },
      Status: (a: ITopicDnDResponse, b: ITopicDnDResponse) => {
        return sortOrder ? b.Status.Order - a.Status.Order : a.Status.Order - b.Status.Order;
      },
      Type: (a: ITopicDnDResponse, b: ITopicDnDResponse) => {
        return a.Type.Name.localeCompare(b.Type.Name) * (sortOrder ? -1 : 1);
      },
    }
  }, []);

  const filteredActivities = useMemo(() => {
    let orderedListActivities = complexfilterActivities<ITopicDnDResponse, typeof enumToStringValueMap>({
      filters,
      array: listActivitiesFormat,
      enumToStringValueMap,
      updatedDateIds: isFetchingUpdatedDate ? undefined : dataUpdatedDate,
    });

    if (searchActivity) {
      orderedListActivities = filterActivities(orderedListActivities, searchActivity);
    }
    if (orderOpt?.type) {
      orderedListActivities = orderedListActivities.sort(sorterActivitiesTable(orderOpt?.sortDirection)[orderOpt.type]);
    }
    return orderedListActivities;
  }, [
    filters,
    listActivitiesFormat,
    searchActivity,
    orderOpt?.type,
    orderOpt?.sortDirection,
    dataUpdatedDate,
    isFetchingUpdatedDate,
    sorterActivitiesTable,
  ]);

  const renderEmpty = useCallback(() => {
    const customDecreasedHeight = showFilters ? 394 : 351;

    if (searchActivity) {
      return <CustomEmptyData
        search
        height={height - customDecreasedHeight}
        searchText={searchActivity}
      />;
    }

    if (hasFilters) {
      return <CustomEmptyData
        height={height - customDecreasedHeight}
        hasFilters={hasFilters}
      />;
    }

    return <CustomEmptyData
      height={height - customDecreasedHeight}
      onOpenCreateActivity={onOpenCreateActivity}
    />;
  }, [searchActivity, showFilters, hasFilters, height]);

  const getResizableColumnWidth = useCallback((resizabledWidth: number): Record<string, number> => ({
    [RESIZABLE_ACTIVITIES_COLUMN_IDS.Identifier]: resizabledWidth * 0.05 > minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.Identifier]
      ? resizabledWidth * 0.05
      : minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.Identifier],
    [RESIZABLE_ACTIVITIES_COLUMN_IDS.Visibility]: resizabledWidth * 0.04 > minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.Visibility]
      ? resizabledWidth * 0.04
      : minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.Visibility],
    [RESIZABLE_ACTIVITIES_COLUMN_IDS.Title]: (resizabledWidth > 1088 && resizabledWidth * 0.266 > minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.Title])
      ? resizabledWidth > 1248 ? resizabledWidth * 0.266 : resizabledWidth * 0.18
      : minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.Title],
    [RESIZABLE_ACTIVITIES_COLUMN_IDS.Type]: resizabledWidth * 0.12 > minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.Type]
      ? resizabledWidth * 0.12
      : minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.Type],
    [RESIZABLE_ACTIVITIES_COLUMN_IDS.Priority]: resizabledWidth * 0.07 > minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.Priority]
      ? resizabledWidth * 0.07
      : minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.Priority],
    [RESIZABLE_ACTIVITIES_COLUMN_IDS.ConstructionSiteDisciplines]: resizabledWidth * 0.14 > minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.ConstructionSiteDisciplines]
      ? resizabledWidth * 0.14
      : minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.ConstructionSiteDisciplines],
    [RESIZABLE_ACTIVITIES_COLUMN_IDS.TopicAssignedUsers]: resizabledWidth * 0.098 > minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.TopicAssignedUsers]
      ? resizabledWidth * 0.098
      : minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.TopicAssignedUsers],
    [RESIZABLE_ACTIVITIES_COLUMN_IDS.DueDate]: resizabledWidth * 0.068 > minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.DueDate]
      ? resizabledWidth * 0.068
      : minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.DueDate],
    [RESIZABLE_ACTIVITIES_COLUMN_IDS.Status]: resizabledWidth * 0.08 > minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.Status]
      ? resizabledWidth * 0.08
      : minWidthResizableActivitiesColumns[RESIZABLE_ACTIVITIES_COLUMN_IDS.Status],
  }), []);

  const resetResizableColumns = useCallback((resizabledWidth: number) => {
    console.log('resetResizableColumns called with resizabledWidth:', resizabledWidth);
    const resizableWidth = getResizableColumnWidth(resizabledWidth);
    console.log('Calculated resizableWidth:', resizableWidth);
    setColumnsWidth(prev => {
      const newState = { ...prev };
      newState.active = false;
      newState.columns = {
        [RESIZABLE_ACTIVITIES_COLUMN_IDS.Identifier]: resizableWidth[RESIZABLE_ACTIVITIES_COLUMN_IDS.Identifier],
        [RESIZABLE_ACTIVITIES_COLUMN_IDS.Visibility]: resizableWidth[RESIZABLE_ACTIVITIES_COLUMN_IDS.Visibility],
        [RESIZABLE_ACTIVITIES_COLUMN_IDS.Title]: resizableWidth[RESIZABLE_ACTIVITIES_COLUMN_IDS.Title],
        [RESIZABLE_ACTIVITIES_COLUMN_IDS.Type]: resizableWidth[RESIZABLE_ACTIVITIES_COLUMN_IDS.Type],
        [RESIZABLE_ACTIVITIES_COLUMN_IDS.Priority]: resizableWidth[RESIZABLE_ACTIVITIES_COLUMN_IDS.Priority],
        [RESIZABLE_ACTIVITIES_COLUMN_IDS.ConstructionSiteDisciplines]: resizableWidth[RESIZABLE_ACTIVITIES_COLUMN_IDS.ConstructionSiteDisciplines],
        [RESIZABLE_ACTIVITIES_COLUMN_IDS.TopicAssignedUsers]: resizableWidth[RESIZABLE_ACTIVITIES_COLUMN_IDS.TopicAssignedUsers],
        [RESIZABLE_ACTIVITIES_COLUMN_IDS.DueDate]: resizableWidth[RESIZABLE_ACTIVITIES_COLUMN_IDS.DueDate],
        [RESIZABLE_ACTIVITIES_COLUMN_IDS.Status]: resizableWidth[RESIZABLE_ACTIVITIES_COLUMN_IDS.Status],
      };
      return newState;
    });
  }, [getResizableColumnWidth]);

  useEffect(() => {
    resetResizableColumns(leftBarAndPaddingWidth);
  }, [leftBarAndPaddingWidth, resetResizableColumns]);

  return {
    columns,
    filteredActivities,
    currentAttachmentIndex,
    leftBarAndPaddingWidth,
    heightAndFiltersShow,
    setCurrentAttachmentIndex,
    renderEmpty,
  };
}

export default useListVision;
